import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Zap } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPWAPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
                               (window.navigator as any).standalone === true;
    setIsStandalone(isInStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
      
      // Show prompt after user has engaged (e.g., 30 seconds or after specific action)
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        const installTime = localStorage.getItem('pwa-prompt-shown-time');
        const now = Date.now();
        
        // Show prompt if not dismissed in last 7 days
        if (!dismissed || (installTime && now - parseInt(installTime) > 7 * 24 * 60 * 60 * 1000)) {
          setShowPrompt(true);
          localStorage.setItem('pwa-prompt-shown-time', now.toString());
        }
      }, 30000); // Show after 30 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  // iOS install instructions
  if (isIOS && showPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 md:bottom-6 md:left-auto md:right-6 md:max-w-md">
        <div className="relative rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 p-6 text-white shadow-2xl">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 rounded-full p-1 hover:bg-white/20 transition"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-4">
            <div className="rounded-full bg-white/20 p-3">
              <Smartphone className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">
                Install Explanatum App
              </h3>
              <p className="text-sm text-blue-50 mb-4">
                Add to your home screen for quick access to your AI Business Consultant
              </p>
              <div className="space-y-2 text-sm bg-white/10 rounded-lg p-3">
                <p>1. Tap the share button <span className="text-xl">⬆️</span></p>
                <p>2. Scroll down and tap "Add to Home Screen"</p>
                <p>3. Tap "Add" to confirm</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop install prompt
  if (showPrompt && deferredPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 md:bottom-6 md:left-auto md:right-6 md:max-w-md">
        <div className="relative rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 p-6 text-white shadow-2xl animate-slide-up">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 rounded-full p-1 hover:bg-white/20 transition"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-4 mb-4">
            <div className="rounded-full bg-white/20 p-3">
              <Download className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">
                Install Explanatum App
              </h3>
              <p className="text-sm text-blue-50">
                Get instant access to your AI Business Consultant. Works offline and loads faster!
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-3 mb-2">
                <Zap className="h-5 w-5 mx-auto" />
              </div>
              <p className="text-xs">Faster</p>
            </div>
            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-3 mb-2">
                <span className="text-xl">💬</span>
              </div>
              <p className="text-xs">Push Alerts</p>
            </div>
            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-3 mb-2">
                <span className="text-xl">📱</span>
              </div>
              <p className="text-xs">Home Screen</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleInstallClick}
              className="flex-1 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50 transition"
            >
              Install Now
            </button>
            <button
              onClick={handleDismiss}
              className="rounded-lg border-2 border-white/30 px-4 py-3 text-sm font-semibold hover:bg-white/10 transition"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPWAPrompt;
